import { useGlobalConfigStore } from '@ms/stores/globalConfig'
import { useBetslipStore } from './index'
import { useSystemBetStore } from '@ms/stores/betslip/useSystemBet.js'
import { useBankerStore } from '@ms/stores/betslip/useBanker.js'

export const whenTax = (callback, defaultValue) => {
  const { taxOfWinning } = useGlobalConfigStore()
  if (taxOfWinning) {
    return callback()
  }
  return {
    initialToReturn: defaultValue,
    toReturn: defaultValue
  }
}

const calcToReturn = (initialToReturn = 0, winningTax = 0, extraBonus = 0) => {
  return initialToReturn - winningTax + extraBonus
}

const getOddsPrize = (odds, stake = 0) => {
  if (!odds || !stake) return 0
  const { potWinMax } = useGlobalConfigStore()
  return Math.min(odds * stake, potWinMax)
}

const getToReturnPlanA = (orderType = '', totalStake = 0) => {
  if (orderType !== 'supple' && orderType !== 'flexible') return
  const { planAOdds } = useBetslipStore()
  return getOddsPrize(planAOdds, totalStake)
}

export const getToReturnPlanB = (orderType = '', totalStake = 0) => {
  if (orderType !== 'supple' && orderType !== 'flexible') return
  const { planBOdds } = useBetslipStore()
  if (!planBOdds) return
  const { totalOddsB1, totalOddsB2 } = planBOdds
  const prize1 = getOddsPrize(totalOddsB1, totalStake)
  const prize2 = getOddsPrize(totalOddsB2, totalStake)
  return { prize1, prize2 }
}

export const getFlexibleTaxInfo = (orderType = '', totalStake = 0) => {
  if (orderType !== 'supple' && orderType !== 'flexible') return
  const initialToReturn = getToReturnPlanA(orderType, totalStake)
  return whenTax(() => {
    const { calcTaxInfo } = useGlobalConfigStore()
    const { winningTax, extraBonus } = calcTaxInfo(initialToReturn, totalStake)
    return {
      initialToReturn,
      winningTax,
      extraBonus,
      toReturn: calcToReturn(initialToReturn, winningTax, extraBonus)
    }
  }, initialToReturn)
}

export const getSuppleTaxInfoFirst = (orderType = '', totalStake = 0, initialToReturn = 0) => {
  if (orderType !== 'supple' && orderType !== 'flexible') return
  return whenTax(() => {
    const { calcTaxInfo } = useGlobalConfigStore()
    const { winningTax, extraBonus } = calcTaxInfo(initialToReturn, totalStake)
    return {
      initialToReturn,
      winningTax,
      extraBonus,
      toReturn: calcToReturn(initialToReturn, winningTax, extraBonus)
    }
  }, initialToReturn)
}

export const getSuppleTaxInfoSecond = (orderType = '', totalStake = 0, initialToReturn = 0) => {
  if (orderType !== 'supple' && orderType !== 'flexible') return
  return whenTax(() => {
    const { calcTaxInfo } = useGlobalConfigStore()
    const { winningTax, extraBonus } = calcTaxInfo(initialToReturn, totalStake)
    return {
      initialToReturn,
      winningTax,
      extraBonus,
      toReturn: calcToReturn(initialToReturn, winningTax, extraBonus)
    }
  }, initialToReturn)
}

export const getStandardSingleTaxInfo = ({ selectionNum, stakeRatio, singleStakeList }) => {
  if (selectionNum === 1 || useBetslipStore().isAllMultiplesMode) {
    return {
      initialToReturn: [0, 0],
      toReturn: [0, 0]
    }
  }

  const { taxOfWinning, potWinMax, calcTaxInfo } = useGlobalConfigStore()
  const initialToReturn = [0, 0]
  const winningTax = [0, 0]
  const extraBonus = [0, 0]

  // 排除互斥比赛
  const betInfo = {}
  singleStakeList.forEach((bet) => {
    const { id, toReturn, stake } = bet
    const eventId = id.split('_')[0]
    if (betInfo[eventId]) {
      betInfo[eventId].push({ id, toReturn, stake })
    } else {
      betInfo[eventId] = [{ id, toReturn, stake }]
    }
  })

  let totalStake = 0
  Object.values(betInfo).forEach((list) => {
    list.sort((a, b) => a.toReturn - b.toReturn)
    const minBet = list[0]
    const maxBet = list[list.length - 1]
    if (minBet.toReturn < initialToReturn[0] || !initialToReturn[0]) {
      initialToReturn[0] = minBet.toReturn
      if (initialToReturn[0] >= potWinMax) {
        initialToReturn[0] = potWinMax
      }
      if (taxOfWinning) {
        const res = calcTaxInfo(initialToReturn[0], minBet.stake, stakeRatio)
        winningTax[0] = res.winningTax
        extraBonus[0] = res.extraBonus
      }
    }
    initialToReturn[1] += maxBet.toReturn
    if (initialToReturn[1] >= potWinMax) {
      initialToReturn[1] = potWinMax
    }
    if (taxOfWinning) {
      totalStake += maxBet.stake
      const res = calcTaxInfo(initialToReturn[1], totalStake, stakeRatio)
      winningTax[1] = res.winningTax
      extraBonus[1] = res.extraBonus
    }
  })

  if (taxOfWinning) {
    return {
      initialToReturn,
      winningTax,
      extraBonus,
      toReturn: [calcToReturn(initialToReturn[0], winningTax[0], extraBonus[0]), calcToReturn(initialToReturn[1], winningTax[1], extraBonus[1])]
    }
  }
  return {
    initialToReturn
  }
}

export const getStandardMultipleTaxInfo = ({ standardSingleTaxInfo, stakeRatio }) => {
  // multiple
  const { standardMultipleStake } = useBetslipStore()
  const { toReturn, stake, count, betCount, bonus } = standardMultipleStake
  if (count === 1) return standardSingleTaxInfo

  const initialToReturn = [0, 0]
  const winningTax = [0, 0]
  const extraBonus = [0, 0]

  const { calcTaxInfo } = useGlobalConfigStore()

  return whenTax(() => {
    if (stake) {
      if (toReturn[0] === toReturn[1]) {
        const value = toReturn[0]
        initialToReturn[0] = value
        initialToReturn[1] = value
        const res = calcTaxInfo(value, stake, stakeRatio)
        const winningTaxValue = res.winningTax
        const extraBonusValue = res.extraBonus
        winningTax[0] = winningTaxValue
        winningTax[1] = winningTaxValue
        extraBonus[0] = extraBonusValue
        extraBonus[1] = extraBonusValue
      } else {
        if (toReturn[0] < initialToReturn[0] || !initialToReturn[0]) {
          initialToReturn[0] = toReturn[0] || 0
          const res = calcTaxInfo(initialToReturn[0], stake, stakeRatio)
          winningTax[0] = res.winningTax
          extraBonus[0] = res.extraBonus
        }

        if (toReturn[1]) {
          const totalStake = stake * betCount
          const maxToReturn = toReturn[1]
          initialToReturn[1] += maxToReturn
          const res = calcTaxInfo(maxToReturn, totalStake, stakeRatio)
          winningTax[1] += res.winningTax
          extraBonus[1] += res.extraBonus
        }
      }
    }

    return {
      initialToReturn,
      bonus,
      winningTax,
      extraBonus,
      toReturn: [calcToReturn(initialToReturn[0], winningTax[0], extraBonus[0]), calcToReturn(initialToReturn[1], winningTax[1], extraBonus[1])]
    }
  }, toReturn)
}

/**
 * 计算 all multiples，需要排除互斥比赛
 * @param {Array} initialToReturn
 * @param {Array} winningTax
 * @param {Array} extraBonus
 * @param {Number} stakeRatio
 */
export const calcAllMultiplesTaxInfo = ({ initialToReturn, winningTax, extraBonus, stakeRatio, selectionList }) => {
  const { potWinMax, taxOfWinning, calcTaxInfo } = useGlobalConfigStore()
  const { systemStakeList } = useSystemBetStore()
  let realTotalStake = 0
  systemStakeList.forEach((bet) => {
    const { toReturn, stake, count, betCount } = bet
    if (count === 1) {
      const { bankerTotalOdds } = useBankerStore()
      if (bankerTotalOdds > 1) {
        let toReturn = bankerTotalOdds * stake
        if (toReturn >= potWinMax) {
          toReturn = potWinMax
        }
        initialToReturn[0] = toReturn
        initialToReturn[1] = toReturn
        const res = calcTaxInfo(toReturn, stake, stakeRatio)
        winningTax[0] = res.winningTax
        winningTax[1] = res.winningTax
        extraBonus[0] = res.extraBonus
        extraBonus[1] = res.extraBonus
        realTotalStake += stake
        return
      }

      // 排除互斥比赛
      const betInfo = {}
      selectionList.forEach((selection) => {
        const { eventId, outcomeInfo } = selection
        const odds = +outcomeInfo.odds
        if (betInfo[eventId]) {
          betInfo[eventId].push(odds)
        } else {
          betInfo[eventId] = [odds]
        }
      })

      let totalStake = 0
      Object.values(betInfo).forEach((list) => {
        list.sort((a, b) => a - b)
        const minToReturn = list[0] * stake
        const maxToReturn = list[list.length - 1] * stake
        realTotalStake += stake

        if (minToReturn < initialToReturn[0] || !initialToReturn[0]) {
          initialToReturn[0] = minToReturn
          if (initialToReturn[0] >= potWinMax) {
            initialToReturn[0] = potWinMax
          }
          if (taxOfWinning) {
            const res = calcTaxInfo(initialToReturn[0], stake, stakeRatio)
            winningTax[0] = res.winningTax
            extraBonus[0] = res.extraBonus
          }
        }
        initialToReturn[1] += maxToReturn
        if (initialToReturn[1] >= potWinMax) {
          initialToReturn[1] = potWinMax
        }
        if (taxOfWinning) {
          totalStake += stake
          const res = calcTaxInfo(initialToReturn[1], totalStake, stakeRatio)
          winningTax[1] = res.winningTax
          extraBonus[1] = res.extraBonus
        }
      })
    } else {
      if (toReturn[0] < initialToReturn[0] || !initialToReturn[0]) {
        initialToReturn[0] = toReturn[0] || 0
        if (taxOfWinning) {
          const res = calcTaxInfo(initialToReturn[0], stake, stakeRatio)
          winningTax[0] = res.winningTax
          extraBonus[0] = res.extraBonus
        }
      }
      if (toReturn[1]) {
        const totalStake = stake * betCount
        realTotalStake += totalStake
        const maxToReturn = toReturn[1]
        initialToReturn[1] += maxToReturn
        if (taxOfWinning) {
          const res = calcTaxInfo(maxToReturn, totalStake, stakeRatio)
          winningTax[1] += res.winningTax
          extraBonus[1] += res.extraBonus
        }
      }
    }
  })

  // 达到最大返奖金额时，需要汇总计算 winning tax & extra bonus
  if (initialToReturn[0] >= potWinMax || initialToReturn[1] >= potWinMax) {
    const res = calcTaxInfo(potWinMax, realTotalStake, stakeRatio)
    if (initialToReturn[0] >= potWinMax) {
      initialToReturn[0] = potWinMax
      winningTax[0] = res.winningTax
      extraBonus[0] = res.extraBonus
    }

    if (initialToReturn[1] >= potWinMax) {
      initialToReturn[1] = potWinMax
      winningTax[1] = res.winningTax
      extraBonus[1] = res.extraBonus
    }
  }
}

export const calcStandardSingleTaxInfo = ({ initialToReturn, winningTax, extraBonus, standardSingleTaxInfo }) => {
  const { potWinMax, taxOfWinning } = useGlobalConfigStore()
  if (standardSingleTaxInfo.initialToReturn[0] < initialToReturn[0] || !initialToReturn[0]) {
    initialToReturn[0] = standardSingleTaxInfo.initialToReturn[0]
    if (taxOfWinning) {
      winningTax[0] = standardSingleTaxInfo.winningTax[0]
      extraBonus[0] = standardSingleTaxInfo.extraBonus[0]
    }
  }

  initialToReturn[1] = standardSingleTaxInfo.initialToReturn[1]
  if (taxOfWinning && initialToReturn[1] <= potWinMax) {
    winningTax[1] = standardSingleTaxInfo.winningTax[1]
    extraBonus[1] = standardSingleTaxInfo.extraBonus[1]
  }
}

export const calcStandardMultipleTaxInfo = ({ orderType, initialToReturn, bonus, winningTax, extraBonus, standardMultipleTaxInfo }) => {
  if (!standardMultipleTaxInfo.initialToReturn) return
  const { potWinMax, taxOfWinning } = useGlobalConfigStore()
  initialToReturn[0] = standardMultipleTaxInfo.initialToReturn[0]
  initialToReturn[1] += standardMultipleTaxInfo.initialToReturn[1]

  if (orderType === 'multiple' && standardMultipleTaxInfo.bonus) {
    // initial to return 需要排除 bonus
    const [minBonus, maxBonus] = standardMultipleTaxInfo.bonus
    bonus[0] = minBonus
    bonus[1] = maxBonus
    initialToReturn[0] -= minBonus
    initialToReturn[1] -= maxBonus
  }

  if (taxOfWinning) {
    winningTax[0] = standardMultipleTaxInfo.winningTax[0]
    extraBonus[0] = standardMultipleTaxInfo.extraBonus[0]

    if (initialToReturn[1] < potWinMax) {
      winningTax[1] += standardMultipleTaxInfo.winningTax[1]
      extraBonus[1] += standardMultipleTaxInfo.extraBonus[1]
    }
  }
}

export const calcSingleStakeTaxInfo = ({ initialToReturn, winningTax, extraBonus, standardSingleTaxInfo }) => {
  const { potWinMax, taxOfWinning } = useGlobalConfigStore()
  if (standardSingleTaxInfo.initialToReturn[0] < initialToReturn[0] || !initialToReturn[0]) {
    initialToReturn[0] = standardSingleTaxInfo.initialToReturn[0]
    if (taxOfWinning) {
      const minWinningTax = standardSingleTaxInfo.winningTax[0]
      if (minWinningTax < winningTax[0]) {
        winningTax[0] = minWinningTax
      }

      const minExtraBonus = standardSingleTaxInfo.extraBonus[0]
      if (minExtraBonus < extraBonus[0]) {
        extraBonus[0] = minExtraBonus
      }
    }
  }

  initialToReturn[1] += standardSingleTaxInfo.initialToReturn[1]
  if (taxOfWinning && initialToReturn[1] < potWinMax) {
    winningTax[1] += standardSingleTaxInfo.winningTax[1]
    extraBonus[1] += standardSingleTaxInfo.extraBonus[1]
  }
}

export const getTaxInfo = ({ isAllMultiplesMode, initialToReturn, bonus, winningTax, extraBonus, stakeRatio, totalStake, orderType }) => {
  const { potWinMax, calcTaxInfo } = useGlobalConfigStore()

  if (initialToReturn[0] >= potWinMax) {
    initialToReturn[0] = potWinMax
  }

  if (initialToReturn[1] >= potWinMax) {
    initialToReturn[1] = potWinMax
  }

  return whenTax(() => {
    if (!isAllMultiplesMode && orderType !== 'single' && (initialToReturn[0] >= potWinMax || initialToReturn[1] >= potWinMax)) {
      // 达到最大返奖金额时，需要汇总计算 winning tax & extra bonus
      const res = calcTaxInfo(potWinMax, totalStake, stakeRatio)
      if (initialToReturn[0] >= potWinMax) {
        initialToReturn[0] = potWinMax
        winningTax[0] = res.winningTax
        extraBonus[0] = res.extraBonus
      }

      if (initialToReturn[1] >= potWinMax) {
        initialToReturn[1] = potWinMax
        winningTax[1] = res.winningTax
        extraBonus[1] = res.extraBonus
      }
    }

    return {
      initialToReturn,
      bonus,
      winningTax,
      extraBonus,
      toReturn: [initialToReturn[0] + bonus[0] - winningTax[0] + extraBonus[0], initialToReturn[1] + bonus[1] - winningTax[1] + extraBonus[1]]
    }
  }, initialToReturn)
}
